import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';

// Local taxi services, shared by both languages so phone numbers never drift apart.
const taxiServices = {
  closest: [
    { name: 'Taxi VSL conventionné CPAM', town: 'Saint-Lubin-des-Joncherets', phone: '+33 6 59 48 25 76' },
  ],
  eureEtLoir: [
    { name: 'Anet Taxi', town: 'Anet', phone: '+33 6 81 50 76 41' },
    { name: 'Alizé Taxi', town: 'Pullay', phone: '+33 2 32 32 11 96' },
    { name: 'Eure Taxi', town: "Verneuil d'Avre et d'Iton", phone: '+33 2 32 32 02 48' },
    { name: 'Taxi VSL conventionné', town: "Verneuil d'Avre et d'Iton", phone: '+33 7 63 78 75 48' },
  ],
  dreux: [
    { name: 'Taxi Dreux (Taxi MB)', town: 'Dreux', phone: '+33 6 61 26 64 03' },
    { name: 'Taxi du Drouais', town: 'Dreux', phone: '+33 6 07 81 39 99' },
    { name: 'Taxi Géo Dreux 28', town: 'Dreux', phone: '+33 6 80 65 96 10' },
    { name: 'Central Taxi Dreux', town: 'Dreux', phone: '+33 2 37 42 36 00' },
  ],
};

const buildTaxiList = (title, labels) => ({
  taxiList: {
    title,
    groups: [
      { label: labels.closest, services: taxiServices.closest },
      { label: labels.eureEtLoir, services: taxiServices.eureEtLoir },
      { label: labels.dreux, services: taxiServices.dreux },
    ],
  },
});

const faqData = {
  en: [
    {
      id: 'getting-here',
      question: 'How do I get to Château des Joncherets?',
      subItems: [
        {
          question: '🚆 By train',
          answer: [
            { text: "From Gare Montparnasse, take the N line (Transilien) towards Dreux: approximately 55 minutes." },
            { label: 'Option 1. Nonancourt station:', text: "A 15-minute walk to the château. Only 4 trains per day, so check SNCF Connect to plan ahead." },
            { label: 'Option 2. Dreux station:', text: "Trains run hourly. From Dreux, take a taxi or rideshare to the château (15–20 minutes, around €20–30)." },
          ],
        },
        {
          question: '🚕 By taxi or rideshare from Paris',
          answer: [
            { text: "Expect around €80–100 from Paris." },
            { text: "Uber and Bolt both work well from Paris." },
            { text: "Once you are in the area, Uber and Bolt are far less readily available.", faqLink: { id: 'local-taxis', label: 'How can I find a taxi near the château?' } },
          ],
        },
        {
          question: '🚗 By car',
          answer: [
            { text: "Recommended if you'd like more freedom to explore over the weekend." },
            { label: 'Rental:', text: "Europcar, Hertz, Sixt, Avis and Enterprise have agencies at train stations and in the city. Getaround offers peer-to-peer rentals." },
            { text: "Free parking is available at Place Alsace Lorraine, a 3-minute walk (200m) from the château entrance." },
          ],
        },
      ],
    },
    {
      id: 'local-taxis',
      question: 'How can I find a taxi near the château?',
      answer: [
        { text: "In addition to Uber and Bolt, which exist but are less readily available in the area surrounding the château than in Paris, please find below a list of well-rated local taxi services:" },
        buildTaxiList('Taxi services in the Château des Joncherets area', {
          closest: 'Closest (same town as the Château)',
          eureEtLoir: 'Eure-et-Loir region (closest surrounding areas)',
          dreux: 'Dreux (further away, about 30 km)',
        }),
      ],
    },
    {
      id: 'parking',
      question: 'Where can I park?',
      answer: [
        { text: "Free public parking is available at Place Alsace Lorraine, a 3-minute walk (200 metres) from the Château entrance. There is no on-site guest parking at the Château itself.", link: { url: 'https://maps.app.goo.gl/gKziZAPhmBQdnppR6', label: 'View on Google Maps' } },
        { text: "The lot sits near the town centre and is well-lit in the evening. From there, follow Rue de la Madeleine down to the Château gates — it's a flat, easy walk." },
        { map: { src: 'https://maps.google.com/maps?saddr=5+Rue+de+l%27Appel+du+18+Juin,+27320+Nonancourt,+France&daddr=1+Rue+de+l%27%C3%89glise,+28350+Saint-Lubin-des-Joncherets,+France&hl=en&z=16&output=embed', title: 'Walking route from the parking to the Château des Joncherets entrance' } },
      ],
    },
    {
      id: 'shuttle-home',
      question: 'How do I get home after the wedding?',
      answer: [
        { textKey: 'scheduleShuttleNote' },
      ],
    },
    {
      id: 'accessibility',
      question: 'Is the venue accessible for guests with reduced mobility?',
      answer: [
        { text: "All event spaces within the château are accessible without the need for stairs, ensuring ease of movement throughout the celebration. Please note that some parts of the château itself (outside event areas) may have limited access." },
      ],
    },
    {
      id: 'coat-check',
      question: 'Is there somewhere to store my coat or luggage during the event?',
      answer: [
        { text: "A coat check will be available on-site, where you may securely leave coats or small luggage during the festivities." },
      ],
    },
    {
      id: 'dress-code',
      question: 'What is the dress code?',
      answer: [
        { label: 'Friday evening & Sunday brunch:', text: "Casual and relaxed" },
        { label: 'Saturday celebration:', text: "Formal attire—dress to impress!" },
      ],
    },
    {
      id: 'weather',
      question: 'What is the weather like in early September?',
      answer: [
        { text: "In Nonancourt, early September typically offers beautiful late-summer weather. Based on historical data over the past decade, September 5th usually sees:" },
        { label: 'Average daytime temperature:', text: "around 22°C (72°F)" },
        { label: 'Average evening temperature:', text: "around 12–14°C (54–57°F)" },
        { text: "Expect mild, pleasant conditions during the day, with cooler temperatures in the evening—perfect for an outdoor celebration, though we recommend bringing a light layer for after sunset." },
      ],
    },
    {
      id: 'kids',
      question: 'Are kids invited?',
      answer: [
        { text: "We love your little ones, but for this celebration we are only able to accommodate children within our immediate family. We truly appreciate your understanding and can't wait to celebrate with you!" },
      ],
    },
    {
      id: 'pets',
      question: 'Are pets allowed?',
      answer: [
        { text: "We adore your furry friends, but unfortunately pets are not permitted at the Château." },
      ],
    },
  ],
  fr: [
    {
      id: 'getting-here',
      question: 'Comment rejoindre le Château des Joncherets ?',
      subItems: [
        {
          question: '🚆 En train',
          answer: [
            { text: "Depuis la Gare Montparnasse, prenez la ligne N (Transilien) en direction de Dreux : environ 55 minutes." },
            { label: 'Option 1. Gare de Nonancourt :', text: "15 minutes à pied du château. Seulement 4 trains par jour : pensez à vérifier SNCF Connect pour planifier votre trajet." },
            { label: 'Option 2. Gare de Dreux :', text: "Trains toutes les heures. Depuis Dreux, taxi ou VTC jusqu'au château (15–20 minutes, environ 20–30 €)." },
          ],
        },
        {
          question: '🚕 En taxi ou VTC depuis Paris',
          answer: [
            { text: "Comptez environ 80–100 € depuis Paris." },
            { text: "Uber et Bolt fonctionnent bien depuis Paris." },
            { text: "Sur place, Uber et Bolt sont beaucoup moins disponibles.", faqLink: { id: 'local-taxis', label: 'Comment trouver un taxi près du château ?' } },
          ],
        },
        {
          question: '🚗 En voiture',
          answer: [
            { text: "Recommandée si vous souhaitez plus de liberté pour profiter de la région tout au long du week-end." },
            { label: 'Location :', text: "Europcar, Hertz, Sixt, Avis et Enterprise disposent d'agences dans les gares et en ville. Getaround propose de la location entre particuliers." },
            { text: "Un parking gratuit est disponible Place Alsace Lorraine, à 3 minutes à pied (200 m) de l'entrée du château." },
          ],
        },
      ],
    },
    {
      id: 'local-taxis',
      question: 'Comment trouver un taxi près du château ?',
      answer: [
        { text: "En plus d'Uber et de Bolt, qui existent mais sont moins disponibles dans les environs du château qu'à Paris, voici une liste de services de taxi locaux bien notés :" },
        buildTaxiList('Services de taxi dans la région du Château des Joncherets', {
          closest: 'Le plus proche (même commune que le Château)',
          eureEtLoir: 'Région Eure-et-Loir (environs les plus proches)',
          dreux: 'Dreux (plus éloigné, à environ 30 km)',
        }),
      ],
    },
    {
      id: 'parking',
      question: 'Où puis-je me garer ?',
      answer: [
        { text: "Un parking public gratuit est disponible Place Alsace Lorraine, à 3 minutes à pied (200 mètres) de l'entrée du Château. Le Château ne dispose pas de parking invités sur place.", link: { url: 'https://maps.app.goo.gl/gKziZAPhmBQdnppR6', label: 'Voir sur Google Maps' } },
        { text: "Le parking se trouve près du centre-ville et est bien éclairé en soirée. De là, suivez la Rue de la Madeleine jusqu'aux grilles du Château — une promenade facile sur terrain plat." },
        { map: { src: 'https://maps.google.com/maps?saddr=5+Rue+de+l%27Appel+du+18+Juin,+27320+Nonancourt,+France&daddr=1+Rue+de+l%27%C3%89glise,+28350+Saint-Lubin-des-Joncherets,+France&hl=fr&z=16&output=embed', title: 'Itinéraire à pied du parking jusqu\'à l\'entrée du Château des Joncherets' } },
      ],
    },
    {
      id: 'shuttle-home',
      question: 'Comment rentrer après le mariage ?',
      answer: [
        { textKey: 'scheduleShuttleNote' },
      ],
    },
    {
      id: 'accessibility',
      question: 'Le lieu est-il accessible aux personnes à mobilité réduite ?',
      answer: [
        { text: "Tous les espaces de réception du château sont accessibles sans escaliers, permettant une circulation facile tout au long de l'événement. Veuillez noter que certaines parties du château (en dehors des espaces de réception) peuvent être moins accessibles." },
      ],
    },
    {
      id: 'coat-check',
      question: 'Y a-t-il un endroit où déposer mon manteau ou mes bagages pendant les festivités ?',
      answer: [
        { text: "Un vestiaire sera disponible sur place, où vous pourrez déposer manteaux et petits bagages en toute sécurité pendant les festivités." },
      ],
    },
    {
      id: 'dress-code',
      question: 'Quel est le dress code ?',
      answer: [
        { label: 'Vendredi soir & brunch du dimanche :', text: "Tenue décontractée et confortable" },
        { label: 'Célébration du samedi :', text: "Tenue formelle — sortez le grand jeu !" },
      ],
    },
    {
      id: 'weather',
      question: 'Quel temps fait-il à Nonancourt début septembre ?',
      answer: [
        { text: "À Nonancourt, le début du mois de septembre offre généralement une météo douce de fin d'été. D'après les données des dix dernières années, le 5 septembre présente en moyenne :" },
        { label: 'Température en journée :', text: "environ 22°C" },
        { label: 'Température en soirée :', text: "environ 12 à 14°C" },
        { text: "Les conditions sont généralement agréables et douces en journée, avec une fraîcheur en soirée — idéales pour une célébration en extérieur. Nous vous conseillons toutefois de prévoir une petite veste pour après le coucher du soleil." },
      ],
    },
    {
      id: 'kids',
      question: 'Les enfants sont-ils invités ?',
      answer: [
        { text: "Nous adorons vos enfants, mais pour cette célébration, nous ne pouvons malheureusement accueillir que les enfants de notre famille proche. Nous vous remercions sincèrement pour votre compréhension et avons hâte de célébrer avec vous !" },
      ],
    },
    {
      id: 'pets',
      question: 'Les animaux de compagnie sont-ils autorisés ?',
      answer: [
        { text: "Nous adorons vos compagnons à quatre pattes, mais malheureusement, nous ne pouvons accueillir les animaux de compagnie au Château." },
      ],
    },
  ],
};

// Lets an answer link to another FAQ item: opens it and scrolls it into view.
const FAQNavContext = createContext(() => {});

const MapEmbed = ({ src, title, className }) => (
  <div className={`rounded-2xl overflow-hidden border border-[#E5E5E5] ${className}`}>
    <iframe
      src={src}
      title={title}
      loading="lazy"
      referrerPolicy="no-referrer-when-downgrade"
      className="w-full h-full block"
      style={{ border: 0 }}
      allowFullScreen
    />
  </div>
);

const AnswerBody = ({ answer }) => {
  const { t } = useLanguage();
  const goToFAQItem = useContext(FAQNavContext);
  const hasBullets = answer.length > 1 || answer[0].label || answer[0].faqLink;
  // textKey pulls shared copy from the translations, so an answer that also
  // appears elsewhere on the site cannot drift out of sync with it.
  const textOf = (item) => (item.textKey ? t(item.textKey) : item.text);
  const mapItem = answer.find((i) => i.map);

  const renderItem = (item, i) => {
    if (item.map) {
      return (
        <div key={i} className="mt-3 h-80 sm:h-96 md:h-[28rem]">
          <MapEmbed src={item.map.src} title={item.map.title} className="h-full" />
        </div>
      );
    }
    if (item.faqLink) {
      return (
        <p key={i}>
          {textOf(item)}{' '}
          <button
            type="button"
            onClick={() => goToFAQItem(item.faqLink.id)}
            className="text-left text-[#B5985A] hover:underline"
          >
            {item.faqLink.label}
          </button>
        </p>
      );
    }
    if (item.taxiList) {
      return (
        <div key={i} className="mt-2 space-y-3">
          <p className="font-semibold text-[#2D2A26]">{item.taxiList.title}</p>
          {item.taxiList.groups.map((group, groupIndex) => (
            <div key={groupIndex}>
              <p className="font-medium text-[#2D2A26]">{group.label}</p>
              <ul className="mt-1 space-y-1">
                {group.services.map((service, serviceIndex) => (
                  <li key={serviceIndex} className="flex gap-2">
                    <span className="flex-shrink-0 text-[#B5985A]">•</span>
                    <span className="min-w-0">
                      <span className="text-[#2D2A26]">{service.name}</span>{' '}
                      <span className="text-[#8A8880]">({service.town})</span>{' '}
                      <a
                        href={`tel:${service.phone.replace(/\s/g, '')}`}
                        className="text-[#B5985A] hover:underline whitespace-nowrap"
                      >
                        {service.phone}
                      </a>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      );
    }
    if (item.label) {
      return (
        <p key={i}>
          <span className="font-semibold text-[#2D2A26]">{item.label}</span>{' '}
          {textOf(item)}
          {item.link && (
            <> <a href={item.link.url} target="_blank" rel="noopener noreferrer" className="text-[#B5985A] hover:underline">{item.link.label}</a></>
          )}
        </p>
      );
    }
    return (
      <p key={i}>
        {textOf(item)}
        {item.link && (
          <> <a href={item.link.url} target="_blank" rel="noopener noreferrer" className="text-[#B5985A] hover:underline">{item.link.label}</a></>
        )}
      </p>
    );
  };

  if (!hasBullets) {
    return <p>{textOf(answer[0])}</p>;
  }
  if (mapItem) {
    return (
      <div className="flex flex-col lg:flex-row lg:gap-5 lg:items-start">
        <div className="min-w-0 space-y-2 lg:flex-1">
          {answer.filter((i) => !i.map).map((item, i) => renderItem(item, i))}
        </div>
        <div className="mt-3 lg:mt-0 lg:w-[30rem] lg:flex-shrink-0 h-80 sm:h-96 lg:h-96">
          <MapEmbed src={mapItem.map.src} title={mapItem.map.title} className="h-full" />
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-2">
      {answer.map((item, i) => renderItem(item, i))}
    </div>
  );
};

const SubFAQItem = ({ question, answer, isOpen, onToggle }) => (
  <div className="border-b border-[#EFEFEF] last:border-b-0">
    <button
      onClick={onToggle}
      className="w-full flex items-center gap-3 py-3 text-left group"
      aria-expanded={isOpen}
    >
      <span
        className="flex-shrink-0 text-[#B5985A] text-sm transition-transform duration-300"
        style={{ transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}
      >
        ▶
      </span>
      <span className="font-serif text-base text-[#2D2A26] group-hover:text-[#B5985A] transition-colors duration-200">
        {question}
      </span>
    </button>

    <AnimatePresence initial={false}>
      {isOpen && (
        <motion.div
          key="sub-answer"
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
          className="overflow-hidden"
        >
          <div className="pl-6 pb-4 text-[#5C5A56] text-sm leading-relaxed">
            <AnswerBody answer={answer} />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

const NestedFAQItems = ({ subItems }) => {
  const [openSubItems, setOpenSubItems] = useState(new Set());
  const toggle = (index) => {
    setOpenSubItems((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };
  return (
    <div className="border-l-2 border-[#E5E5E5] pl-4">
      {subItems.map((sub, i) => (
        <SubFAQItem
          key={i}
          question={sub.question}
          answer={sub.answer}
          isOpen={openSubItems.has(i)}
          onToggle={() => toggle(i)}
        />
      ))}
    </div>
  );
};

const FAQItem = ({ question, answer, subItems, isOpen, onToggle, itemRef }) => {
  return (
    <div ref={itemRef} className="scroll-mt-24 border-b border-[#E5E5E5] last:border-b-0">
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-3 py-5 text-left group"
        aria-expanded={isOpen}
      >
        <span
          className="flex-shrink-0 mt-1 text-[#B5985A] transition-transform duration-300"
          style={{ transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}
        >
          ▶
        </span>
        <span className="font-serif text-xl text-[#2D2A26] group-hover:text-[#B5985A] transition-colors duration-200">
          {question}
        </span>
      </button>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            key="answer"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="pl-7 pb-5 text-[#5C5A56] text-sm leading-relaxed">
              {subItems ? (
                <NestedFAQItems subItems={subItems} />
              ) : (
                <AnswerBody answer={answer} />
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const FAQPage = () => {
  const { t, language } = useLanguage();
  const [openItems, setOpenItems] = useState(new Set());
  const items = faqData[language] || faqData.en;

  const itemRefs = useRef({});
  const [pendingScrollId, setPendingScrollId] = useState(null);

  const toggle = (index) => {
    setOpenItems((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const goToFAQItem = (id) => {
    const index = items.findIndex((item) => item.id === id);
    if (index === -1) return;
    setOpenItems((prev) => new Set(prev).add(index));
    setPendingScrollId(id);
  };

  // Scroll once the target item has actually opened, so we land on its
  // expanded position rather than where it sat while still collapsed.
  useEffect(() => {
    if (!pendingScrollId) return;
    itemRefs.current[pendingScrollId]?.scrollIntoView({ block: 'start' });
    setPendingScrollId(null);
  }, [pendingScrollId]);

  return (
    <section className="pt-14 pb-14 bg-[#FAFAFA]" data-testid="faq-page">
      <div className="max-w-5xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <div className="text-center mb-12">
            <h2 className="font-serif text-4xl sm:text-5xl text-[#2D2A26] mb-4">
              {t('faqTitle')}
            </h2>
            <div className="w-12 h-0.5 bg-[#B5985A] mx-auto" />
          </div>

          <FAQNavContext.Provider value={goToFAQItem}>
            <div className="border border-[#E5E5E5] rounded-sm bg-white px-6">
              {items.map((item, index) => (
                <FAQItem
                  key={item.id}
                  itemRef={(el) => {
                    itemRefs.current[item.id] = el;
                  }}
                  question={item.question}
                  answer={item.answer}
                  subItems={item.subItems}
                  isOpen={openItems.has(index)}
                  onToggle={() => toggle(index)}
                />
              ))}
            </div>
          </FAQNavContext.Provider>
        </motion.div>
      </div>
    </section>
  );
};
